import { useMemo, useState, useEffect } from 'react';
import { SupplyCard, type SupplyItem } from '../components/SupplyCard';
import { SEO } from '../components/SEO';
import {
  generateOrganizationSchema,
  generateProductSchema,
  getBaseUrl,
} from '../utils/seo';
import { supplyService } from '../services/supplyService';
import './SuppliesPage.css';

/**
 * SEO-optimized Supplies Page
 */
export function SuppliesPage() {
  const [supplies, setSupplies] = useState<SupplyItem[]>([]);
  const [suppliesLoading, setSuppliesLoading] = useState(true);

  // Fetch supplies on mount
  useEffect(() => {
    const loadSupplies = async () => {
      try {
        setSuppliesLoading(true);
        const fetchedSupplies = await supplyService.getSupplies();
        setSupplies(fetchedSupplies);
      } catch (error) {
        console.error('Failed to load supplies:', error);
        // Fallback to cached supplies or empty array
        const cachedSupplies = supplyService.getSuppliesSync();
        setSupplies(cachedSupplies);
      } finally {
        setSuppliesLoading(false);
      }
    };

    loadSupplies();
  }, []);

  // Group supplies by category
  const suppliesByCategory = useMemo(() => {
    const grouped: Record<string, SupplyItem[]> = {};
    supplies.forEach((supply) => {
      if (!grouped[supply.category]) {
        grouped[supply.category] = [];
      }
      grouped[supply.category].push(supply);
    });
    return grouped;
  }, [supplies]);

  const handleSupplyPress = (supply: SupplyItem) => {
    if (supply.affiliateUrl) {
      window.open(supply.affiliateUrl, '_blank', 'noopener,noreferrer');
    }
  };

  // Generate structured data
  const structuredData = useMemo(() => {
    const baseUrl = getBaseUrl();
    const data = [
      generateOrganizationSchema(baseUrl),
    ];

    // Add Product schema for each supply item
    supplies.forEach((supply) => {
      data.push(
        generateProductSchema({
          name: supply.title,
          description: supply.description,
          url: supply.affiliateUrl,
        })
      );
    });

    return data;
  }, [supplies]);

  return (
    <>
      <SEO
        title="Research Supplies - Peptide Finder"
        description="Find research supplies including syringes, bacteriostatic water, storage solutions, and more for peptide research."
        keywords="peptide supplies, research supplies, syringes, bacteriostatic water, peptide storage"
        structuredData={structuredData}
      />
      <div className="supplies-page">
        <header className="supplies-page-header">
          <div className="supplies-page-container">
            <h1>Research Supplies</h1>
            <p className="supplies-page-subtitle">
              Find everything you need for peptide research
            </p>
          </div>
        </header>

        <main className="supplies-page-main">
          <div className="supplies-page-container">
            {suppliesLoading ? (
              <div className="supplies-page-empty">
                <p>Loading supplies...</p>
              </div>
            ) : supplies.length === 0 ? (
              <div className="supplies-page-empty">
                <p>No supplies found.</p>
              </div>
            ) : (
              <>
                {Object.entries(suppliesByCategory).map(([category, categorySupplies]) => (
                  <section key={category} className="supplies-page-category" aria-labelledby={`category-${category}`}>
                    <h2 id={`category-${category}`} className="supplies-page-category-title">
                      {category}
                    </h2>
                    <div className="supplies-page-list">
                      {categorySupplies.map((supply) => (
                        <SupplyCard
                          key={supply.id}
                          item={supply}
                          onPress={() => handleSupplyPress(supply)}
                        />
                      ))}
                    </div>
                  </section>
                ))}
                <section className="supplies-page-disclosure" aria-label="Affiliate disclosure">
                  <p className="supplies-page-disclosure-text">
                    As an Amazon Associate, this app may earn from qualifying purchases.
                  </p>
                </section>
              </>
            )}
          </div>
        </main>
      </div>
    </>
  );
}

